import { db } from '../../../integrations/firebase';
import { ref, push, set } from 'firebase/database';
import { fillTemplate, TEMPLATE_EVENT_MAP } from '../constants';
import { sendSms } from '../../../core/notifications/smsService';

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null));
const norm = (m) => String(m || '').replace(/\D/g, '').slice(-10);

// Build the customer-facing message for a template.
export const prepareMessage = (ticket, templateKey) => fillTemplate(templateKey, ticket?.ticketNumber);

// Append to tickets/{id}/publicUpdates (section 6).
export const logPublicUpdate = async (ticketId, payload) => {
    const upRef = push(ref(db, `tickets/${ticketId}/publicUpdates`));
    await set(upRef, clean({ ...payload, sentToCustomer: true, createdAt: Date.now() }));
    return upRef.key;
};

// Mirror to the customer timeline.
const addTimeline = async (mobile, entry) => {
    const key = norm(mobile);
    if (!key) return;
    try {
        const tlRef = push(ref(db, `customerBase/${key}/timeline`));
        await set(tlRef, clean({ ...entry, createdAt: Date.now() }));
    } catch (e) { /* non-fatal */ }
};

const userId = (u) => u?.uid || u?.email || 'agent';

// WhatsApp: build a wa.me link; log only AFTER the user confirms it was sent.
export const buildWhatsAppLink = (ticket, templateKey) => {
    const msg = prepareMessage(ticket, templateKey);
    return `https://wa.me/91${norm(ticket.mobile)}?text=${encodeURIComponent(msg)}`;
};

export const confirmWhatsAppSent = async (ticket, templateKey, user) => {
    const message = prepareMessage(ticket, templateKey);
    await logPublicUpdate(ticket.id, { message, templateKey, channel: 'whatsapp', status: 'sent', createdBy: userId(user) });
    await addTimeline(ticket.mobile, {
        type: 'complaint_customer_update', ticketId: ticket.id, ticketNumber: ticket.ticketNumber,
        channel: 'whatsapp', message, createdBy: userId(user),
    });
};

// SMS: route through the central service → backend → Message91.
export const sendSmsUpdate = async (ticket, templateKey, user) => {
    const message = prepareMessage(ticket, templateKey);
    const eventKey = TEMPLATE_EVENT_MAP[templateKey];
    const result = await sendSms({
        eventKey,
        mobile: ticket.mobile,
        variables: { customerName: ticket.customerName || '', ticketNumber: ticket.ticketNumber, companyName: 'Daksha Organics' },
        relatedType: 'complaint_ticket',
        relatedId: ticket.id,
    });

    if (result.status === 'sent') {
        await logPublicUpdate(ticket.id, {
            message, templateKey, channel: 'sms', provider: 'message91', status: 'sent',
            providerMessageId: result.providerMessageId || '', createdBy: userId(user),
        });
        await addTimeline(ticket.mobile, {
            type: 'complaint_sms_update', ticketId: ticket.id, ticketNumber: ticket.ticketNumber,
            templateKey, message, createdBy: userId(user),
        });
    } else if (result.status === 'failed') {
        await logPublicUpdate(ticket.id, {
            message, templateKey, channel: 'sms', provider: 'message91', status: 'failed',
            errorMessage: result.errorMessage || '', createdBy: userId(user),
        });
    }
    // not_configured / disabled / skipped: surfaced to UI, not logged as a send
    return result;
};

// Copy-to-clipboard fallback when the SMS gateway is not configured.
export const copySmsText = async (ticket, templateKey) => {
    const message = prepareMessage(ticket, templateKey);
    try { await navigator.clipboard.writeText(message); } catch (e) { /* ignore */ }
    return message;
};
